import React, {useState} from 'react';
import {BadgeCheck, Mail, Phone, Send} from 'lucide-react';
import {Button, Form, Input, message, Select} from 'antd';
import {useI18n} from '../../i18n/useI18n';
import {contactMessageService} from '../../services/contactMessageService';
import styles from './styles/ContactPanel.module.css';

const ContactPanel: React.FC = () => {
    const {t} = useI18n();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            await contactMessageService.submit({
                subject: values.subject || '官网咨询',
                content: values.note || '',
                salutation: values.name || '未知',
                contactEmail: values.contact || '',
                phone: values.experience || '',
            });
            message.success(t('site.contactPanel.submitSuccess') || '提交成功，我们会尽快与您联系');
            form.resetFields();
        } catch (error: any) {
            if (error.errorFields) return;
            message.error(error?.response?.data?.message || '提交失败，请稍后重试');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className={styles.wrap}>
            <article className={`site-card ${styles.infoCard}`}>
                <span className="site-eyebrow">{t('site.contactPanel.eyebrow')}</span>
                <h2>{t('site.contactPanel.title')}</h2>
                <p>{t('site.contactPanel.description')}</p>
                <div className={styles.contacts}>
                    <div className={styles.contactItem}>
                        <Mail size={18}/>
                        <div>
                            <strong>{t('site.contactPanel.email')}</strong>
                            <span>contact@d-sense.local</span>
                        </div>
                    </div>
                    <div className={styles.contactItem}>
                        <Phone size={18}/>
                        <div>
                            <strong>{t('site.contactPanel.startPath')}</strong>
                            <span>{t('site.contactPanel.startPathValue')}</span>
                        </div>
                    </div>
                    <div className={styles.contactItem}>
                        <BadgeCheck size={18}/>
                        <div>
                            <strong>{t('site.contactPanel.audience')}</strong>
                            <span>{t('site.contactPanel.audienceValue')}</span>
                        </div>
                    </div>
                </div>
            </article>
            <article className={`site-card ${styles.formCard}`}>
                <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
                    <Form.Item label={t('site.contactPanel.name')} name="name"
                               rules={[{required: true, message: t('site.contactPanel.nameRequired') || '请输入称呼'}]}>
                        <Input placeholder={t('site.contactPanel.namePlaceholder')}/>
                    </Form.Item>
                    <Form.Item label={t('site.contactPanel.contact')} name="contact"
                               rules={[{
                                   required: true,
                                   type: 'email',
                                   message: t('site.contactPanel.contactRequired') || '请输入有效邮箱'
                               }]}>
                        <Input placeholder={t('site.contactPanel.contactPlaceholder')}/>
                    </Form.Item>
                    <Form.Item label={t('site.contactPanel.experience')} name="experience">
                        <Input placeholder={t('site.contactPanel.experiencePlaceholder')}/>
                    </Form.Item>
                    <Form.Item label={t('site.contactPanel.focus')} name="focus">
                        <Select
                            options={[
                                {label: t('site.contactPanel.focus.ai'), value: 'ai'},
                                {label: t('site.contactPanel.focus.trade'), value: 'trade'},
                                {label: t('site.contactPanel.focus.risk'), value: 'risk'}
                            ]}
                            placeholder={t('site.contactPanel.focusPlaceholder')}
                        />
                    </Form.Item>
                    <Form.Item label={t('site.contactPanel.note')} name="note">
                        <Input.TextArea placeholder={t('site.contactPanel.notePlaceholder')} rows={5}/>
                    </Form.Item>
                    <div className={styles.actions}>
                        <Button htmlType="submit" icon={<Send size={16}/>} size="large" type="primary"
                                loading={submitting}>
                            {t('site.contactPanel.invite')}
                        </Button>
                        <Button href="/register" htmlType="button" size="large">
                            {t('site.contactPanel.register')}
                        </Button>
                        <Button href="/login" htmlType="button" size="large">
                            {t('site.contactPanel.login')}
                        </Button>
                    </div>
                </Form>
            </article>
        </section>
    );
};

export default ContactPanel;
